import * as Tooltip from '@radix-ui/react-tooltip';
import { isCompanyEmail } from 'company-email-validator';
import { PageProps } from 'gatsby';
import React, { ReactElement, useState, useEffect } from 'react';
import {
  Body,
  Button,
  GlobalTemplate,
  Headline,
  Input,
  Link,
  Meta,
  Textarea,
} from '../../../components';
import { getCalApi } from '@calcom/embed-react';
import { twMerge } from 'tailwind-merge';

export default function ContactSales({ location }: PageProps): ReactElement {
  const workEmail = (location.state as { workEmail?: string })?.workEmail;
  const firstName = (location.state as { firstName?: string })?.firstName;
  const lastName = (location.state as { lastName?: string })?.lastName;
  const showBookDemo = workEmail ? isCompanyEmail(workEmail) : false;

  const [showFirstNameError, setShowFirstNameError] = useState(false);
  const [showLastNameError, setShowLastNameError] = useState(false);
  const [showCompanyNameError, setShowCompanyNameError] = useState(false);
  const [showRoleError, setShowRoleError] = useState(false);
  const [showWorkEmailError, setShowWorkEmailError] = useState(false);
  const [showErrorPrompt, setShowErrorPrompt] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(true);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [showSubmitError, setShowSubmitError] = useState(true);

  const submitSalesForm = async (e) => {};

  useEffect(() => {
    (async function () {
      const cal = await getCalApi({ namespace: '30min' });
      cal('ui', { hideEventTypeDetails: false, layout: 'month_view' });
    })();
  }, []);

  return (
    <GlobalTemplate pageBgColor="bone" navBgColor="bone">
      <div>
        <div className="flex justify-center p-4">
          <div className="mb-52 grid max-w-290 grid-cols-2 gap-8 max-nav:grid-cols-1">
            <div className="z-50 mt-11">
              <Headline level="hero" color="blue">
                Speak with our sales team
              </Headline>

              <div className="my-4 flex">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="min-h-6 min-w-6 shrink-0 translate-y-0.75">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM15.774 10.1333C16.1237 9.70582 16.0607 9.0758 15.6332 8.72607C15.2058 8.37635 14.5758 8.43935 14.226 8.86679L10.4258 13.5116L9.20711 12.2929C8.81658 11.9024 8.18342 11.9024 7.79289 12.2929C7.40237 12.6834 7.40237 13.3166 7.79289 13.7071L9.79289 15.7071C9.99267 15.9069 10.2676 16.0129 10.5498 15.9988C10.832 15.9847 11.095 15.8519 11.274 15.6333L15.774 10.1333Z"
                    fill="#381FD1"
                  />
                </svg>

                <Body className="ml-4" color="blue">
                  Learn how teams like yours streamline and accelerate
                  accessibility with Stark
                </Body>
              </div>

              <div className="mb-4 flex">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="min-h-6 min-w-6 shrink-0 translate-y-0.75">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM15.774 10.1333C16.1237 9.70582 16.0607 9.0758 15.6332 8.72607C15.2058 8.37635 14.5758 8.43935 14.226 8.86679L10.4258 13.5116L9.20711 12.2929C8.81658 11.9024 8.18342 11.9024 7.79289 12.2929C7.40237 12.6834 7.40237 13.3166 7.79289 13.7071L9.79289 15.7071C9.99267 15.9069 10.2676 16.0129 10.5498 15.9988C10.832 15.9847 11.095 15.8519 11.274 15.6333L15.774 10.1333Z"
                    fill="#381FD1"
                  />
                </svg>

                <Body className="ml-4" color="blue">
                  Check out Stark in action, with a live demo, tailored to your
                  team
                </Body>
              </div>

              <div className="mb-4 flex">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="min-h-6 min-w-6 shrink-0 translate-y-0.75">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM15.774 10.1333C16.1237 9.70582 16.0607 9.0758 15.6332 8.72607C15.2058 8.37635 14.5758 8.43935 14.226 8.86679L10.4258 13.5116L9.20711 12.2929C8.81658 11.9024 8.18342 11.9024 7.79289 12.2929C7.40237 12.6834 7.40237 13.3166 7.79289 13.7071L9.79289 15.7071C9.99267 15.9069 10.2676 16.0129 10.5498 15.9988C10.832 15.9847 11.095 15.8519 11.274 15.6333L15.774 10.1333Z"
                    fill="#381FD1"
                  />
                </svg>

                <Body className="ml-4" color="blue">
                  Discuss which plan is right for your team based on the
                  business needs
                </Body>
              </div>

              <img
                alt=""
                src="https://res.cloudinary.com/stark-lab/image/upload/v1761249000/marketing/features/sales__companies_rawshd.webp"
                className="mt-8 max-h-39 sm:ml-4"
              />
            </div>

            <div className="relative">
              <svg
                width="941"
                height="967"
                viewBox="0 0 941 967"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="pointer-events-none absolute -top-50 -left-75 hidden blur-[162px] will-change-[filter] md:block"
                colorInterpolationFilters="sRGB">
                <circle
                  cx="563.967"
                  cy="494.98"
                  r="168.445"
                  fill="#7662F6"></circle>
                <circle
                  cx="748.609"
                  cy="494.978"
                  r="168.445"
                  fill="#4A9EF4"></circle>
                <circle
                  cx="494.861"
                  cy="651.547"
                  r="168.445"
                  fill="#E05534"></circle>
                <circle
                  cx="563.967"
                  cy="743.328"
                  r="168.445"
                  fill="#E98535"></circle>
                <circle
                  cx="732.411"
                  cy="743.328"
                  r="168.445"
                  fill="#F9D749"></circle>
                <circle
                  cx="805.835"
                  cy="604.037"
                  r="168.445"
                  fill="#5CC183"></circle>
              </svg>
              <form
                onSubmit={submitSalesForm}
                id="form"
                data-netlify="true"
                netlify-honeypot="bot-field"
                name="Sales form"
                className="sales-form">
                <div
                  className={twMerge(
                    'mt-1 grid gap-4 rounded-lg bg-white p-6 transition-opacity md:ml-10',
                    showSuccessMessage ? 'opacity-0' : 'opacity-100'
                  )}>
                  <p className="hidden">
                    <label>
                      Don’t fill this out if you’re human:{' '}
                      <input name="bot-field" />
                    </label>
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="First name"
                      name="firstName"
                      placeholder="Jane"
                      isRequired
                      status={showFirstNameError ? 'error' : 'default'}
                      helpText={
                        showFirstNameError ? 'Provide your first name' : ''
                      }
                    />
                    <Input
                      label="Last name"
                      name="lastName"
                      placeholder="Stark"
                      isRequired
                      status={showLastNameError ? 'error' : 'default'}
                      helpText={
                        showLastNameError ? 'Provide your last name' : ''
                      }
                    />
                  </div>

                  <Input
                    type="email"
                    label="Work email"
                    name="workEmail"
                    placeholder="jane@yourcompany.com"
                    isRequired
                    status={showWorkEmailError ? 'error' : 'default'}
                    helpText={
                      showWorkEmailError ? 'Provide your work email' : ''
                    }
                  />

                  <Input
                    label="Company name"
                    name="companyName"
                    placeholder="Your company name"
                    isRequired
                    status={showCompanyNameError ? 'error' : 'default'}
                    helpText={
                      showCompanyNameError ? 'Provide your company name' : ''
                    }
                  />

                  <Input
                    id="role"
                    label="Role"
                    name="role"
                    placeholder="Your role"
                    isRequired
                    status={showRoleError ? 'error' : 'default'}
                    helpText={showRoleError ? 'Provide your role' : ''}
                  />

                  <Textarea
                    label="How can we help?"
                    name="message"
                    placeholder="Tell us a bit about how we can help you improve your current accessibility compliance workflow."
                  />

                  <div className="mt-6">
                    <Button className="w-24" isDisabled={isButtonDisabled}>
                      Submit
                    </Button>
                  </div>

                  {showErrorPrompt && (
                    <div>
                      <Body color="error" className="text-med!">
                        Please fix the errors above before submitting.
                      </Body>
                    </div>
                  )}

                  {showSubmitError && (
                    <div>
                      <Body color="error" className="text-med!">
                        There was a problem submitting your request. Please try
                        again.
                      </Body>
                    </div>
                  )}

                  <Body color="gray" className="text-sm!">
                    By clicking submit, you agree to our{' '}
                    <Link href="/tos">Terms of Service</Link> and{' '}
                    <Link href="/privacy">Privacy Policy</Link>.
                  </Body>
                </div>

                <div
                  className={twMerge(
                    'absolute top-0 mt-24 grid gap-4 rounded-xl bg-white p-12 shadow-md transition-opacity md:ml-10',
                    showSuccessMessage
                      ? 'visible opacity-100'
                      : 'invisible opacity-0'
                  )}>
                  <div className="flex flex-col items-center gap-4">
                    <svg
                      width="64"
                      height="64"
                      viewBox="0 0 250 250"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="rounded-full">
                      <path
                        d="M0 0H250V250H0V0Z"
                        fill="var(--color-base-purple-500)"></path>
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M120.601 201.202V178.293C136.28 179.657 151.758 174.011 162.875 162.872C180.585 145.161 183.585 117.515 170.087 96.4185L79.9909 186.506C76.915 184.234 74.0132 181.734 71.3097 179.029C41.5634 149.286 41.5634 101.06 71.3097 71.314C86.7053 55.8855 107.986 47.8107 129.742 49.1421V72.0504C114.063 70.6862 98.585 76.3325 87.4682 87.4713C69.7586 105.182 66.7584 132.828 80.2565 153.925L170.353 63.8387C173.429 66.1113 176.331 68.6105 179.034 71.3155C208.78 101.059 208.78 149.285 179.034 179.031C163.638 194.459 142.357 202.533 120.601 201.202Z"
                        fill="white"></path>
                    </svg>
                    <Headline level="3" className="text-center">
                      Submitted! Want to <br />
                      book a demo?
                    </Headline>
                    <div
                      data-orientation="horizontal"
                      role="separator"
                      className="my-6 bg-slate-200 data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-px"
                    />
                  </div>
                  <Body>
                    Our Sales team will reach out shortly to discuss your
                    company's accessibility needs and how to get the most out of
                    Stark.
                  </Body>

                  {showBookDemo && (
                    <>
                      <Body>Prefer to talk sooner? Book a demo.</Body>
                      <div>
                        <Button
                          type="button"
                          data-cal-namespace="30min"
                          data-cal-link="catnoone/30min"
                          data-cal-config={`{"layout":"month_view","useSlotsViewOnSmallScreen":"true","name":"${firstName} ${lastName}","email":"${workEmail}"}`}>
                          Book a demo call
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </GlobalTemplate>
  );
}

export const Head = () => (
  <Meta ogImgUrl="https://res.cloudinary.com/stark-lab/image/upload/v1761273066/marketing/meta/homepage_fzpkxd.png">
    <meta name="theme-color" content="#381FD1"></meta>
  </Meta>
);
